/**
 * useTemplates Hook
 *
 * CRUD for email templates via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, EmailTemplate, EmailCreateTemplateInput, EmailUpdateTemplateInput } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { errorMessage } from '../types';

export function useTemplates(accountId: string | undefined) {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: queryKeys.email.templates({ accountId }),
    queryFn: async () => {
      if (!accountId) throw new Error('No account selected');
      return api.listEmailTemplates(accountId);
    },
    enabled: !!accountId,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.email.templates({ accountId }) });
  }, [accountId, queryClient]);

  const createTemplate = useCallback(
    async (input: EmailCreateTemplateInput) => {
      const result = await api.createEmailTemplate(input);
      invalidate();
      return result;
    },
    [invalidate],
  );

  const updateTemplate = useCallback(
    async (templateId: string, input: EmailUpdateTemplateInput) => {
      if (!accountId) return;
      const result = await api.updateEmailTemplate(templateId, accountId, input);
      invalidate();
      return result;
    },
    [accountId, invalidate],
  );

  const deleteTemplate = useCallback(
    async (templateId: string) => {
      if (!accountId) return;
      await api.deleteEmailTemplate(templateId, accountId);
      invalidate();
    },
    [accountId, invalidate],
  );

  return {
    templates: queryResult.data?.templates ?? ([] as EmailTemplate[]),
    loading: queryResult.isLoading,
    error: errorMessage(queryResult.error),
    refresh: queryResult.refetch,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
}
